<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import LoadingButton from '@/components/app/LoadingButton.vue'
import CodeField from '@/components/app/CodeField.vue'
import EmailOtpForm from '@/components/app/EmailOtpForm.vue'
import ErrorAlert from '@/components/app/ErrorAlert.vue'
import {
  backupCodeVerify,
  emailOtpRequest,
  emailOtpVerify,
  totpVerify,
  type MfaStatus,
  type UpgradedSession,
} from '@/modules/auth/authApi'
import { updateLocalAccessToken } from '@/lib/session'
import { apiErrorMessage } from '@/lib/errors'

/**
 * **Vérification ou enrôlement d'un second facteur** — le même panneau sur la page MFA et
 * dans la modale de step-up. Chaque succès remplace l'access token en place (la famille de
 * refresh tokens a été montée côté serveur) puis émet `verified`.
 */
defineProps<{ status: MfaStatus }>()
const emit = defineEmits<{ verified: []; enroll: [] }>()

const codeField = ref<InstanceType<typeof CodeField> | null>(null)
const mode = ref<'totp' | 'backup' | 'email'>('totp')
const code = ref('')
const error = ref('')
const serverError = ref('')
const busy = ref<'verify' | 'request' | null>(null)

onMounted(async () => {
  await nextTick()
  codeField.value?.focus()
})

watch(mode, async () => {
  await nextTick()
  codeField.value?.focus()
})

function switchMode(next: 'totp' | 'backup' | 'email'): void {
  mode.value = next
  code.value = ''
  error.value = ''
  serverError.value = ''
}

async function switchToEmail(): Promise<void> {
  switchMode('email')
  await requestEmailOtp()
}

function applyUpgrade(data: UpgradedSession): void {
  updateLocalAccessToken(data.accessToken, data.expiresIn)
  emit('verified')
}

async function verifyWith(action: () => Promise<UpgradedSession>): Promise<void> {
  serverError.value = ''
  if (!code.value.trim()) {
    error.value = 'Saisissez le code.'
    return
  }
  busy.value = 'verify'
  try {
    applyUpgrade(await action())
  } catch (err) {
    serverError.value = apiErrorMessage(err, 'Code refusé.')
  } finally {
    busy.value = null
  }
}

const submitTotp = () => verifyWith(() => totpVerify(code.value.trim()))
const submitBackup = () => verifyWith(() => backupCodeVerify(code.value.trim()))
const submitEmailOtp = () => verifyWith(() => emailOtpVerify(code.value.trim()))

async function requestEmailOtp(): Promise<void> {
  serverError.value = ''
  busy.value = 'request'
  try {
    await emailOtpRequest()
  } catch (err) {
    serverError.value = apiErrorMessage(err, 'Envoi du code impossible pour le moment.')
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <div class="grid gap-4">
    <!-- Une application d'authentification est configurée : la vérifier. -->
    <template v-if="status.totp === 'active'">
      <form v-if="mode === 'totp'" class="grid gap-4" novalidate @submit.prevent="submitTotp">
        <p class="text-sm text-muted-foreground">
          Saisissez le code affiché par votre application d’authentification.
        </p>
        <CodeField
          ref="codeField"
          v-model="code"
          label="Code à 6 chiffres"
          :digits="6"
          :error="error"
          @clear="error = ''"
        />
        <ErrorAlert :message="serverError" />
        <LoadingButton type="submit" class="w-full" :loading="busy === 'verify'">Vérifier</LoadingButton>
      </form>

      <form v-else-if="mode === 'backup'" class="grid gap-4" novalidate @submit.prevent="submitBackup">
        <p class="text-sm text-muted-foreground">Saisissez l’un de vos codes de secours (usage unique).</p>
        <CodeField
          ref="codeField"
          v-model="code"
          label="Code de secours"
          hint="Format : XXXXX-XXXXX"
          :error="error"
          @clear="error = ''"
        />
        <ErrorAlert :message="serverError" />
        <LoadingButton type="submit" class="w-full" :loading="busy === 'verify'"
          >Utiliser ce code</LoadingButton
        >
      </form>

      <EmailOtpForm
        v-else
        v-model="code"
        :error="error"
        :server-error="serverError"
        :busy="busy"
        @request="requestEmailOtp"
        @submit="submitEmailOtp"
        @clear="error = ''"
      />

      <nav class="flex flex-col items-center gap-1 text-sm" aria-label="Autres méthodes">
        <Button v-if="mode !== 'totp'" variant="link" size="sm" @click="switchMode('totp')">
          Utiliser l’application d’authentification
        </Button>
        <Button v-if="mode !== 'backup'" variant="link" size="sm" @click="switchMode('backup')">
          Utiliser un code de secours
        </Button>
        <Button v-if="mode !== 'email'" variant="link" size="sm" @click="switchToEmail"
          >Recevoir un code par e-mail</Button
        >
      </nav>
    </template>

    <!-- Aucun facteur : en proposer un. -->
    <template v-else>
      <template v-if="mode !== 'email'">
        <p class="text-sm text-muted-foreground">
          <slot name="proposal">
            Protégez votre compte avec une <strong>double authentification</strong> : configurez une
            application d’authentification ; à défaut, recevez un code par e-mail.
          </slot>
        </p>
        <div class="grid gap-2">
          <Button v-if="status.totpAvailable" class="w-full" @click="emit('enroll')">
            Configurer une application d’authentification
          </Button>
          <LoadingButton
            variant="secondary"
            class="w-full"
            :loading="busy === 'request'"
            @click="switchToEmail"
          >
            Recevoir un code par e-mail
          </LoadingButton>
        </div>
        <ErrorAlert :message="serverError" />
      </template>
      <EmailOtpForm
        v-else
        v-model="code"
        :error="error"
        :server-error="serverError"
        :busy="busy"
        @request="requestEmailOtp"
        @submit="submitEmailOtp"
        @clear="error = ''"
      />
    </template>
  </div>
</template>
